// src/components/LoginModal.js
import React, { useState, useEffect, useRef } from 'react';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import './LoginModal.css';
import GoogleNameModal from './GoogleNameModal';

const LoginModal = ({ currentLanguage, getLocalizedText, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [firstName, setFirstName] = useState('');

  const [showGoogleNameModal, setShowGoogleNameModal] = useState(false);
  const [googleUserData, setGoogleUserData] = useState(null);

  const modalRef = useRef(null);
  const t = (ar, en) => getLocalizedText(ar, en);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        if (!loginSuccess && !showGoogleNameModal) onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, loginSuccess, showGoogleNameModal]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && !loginSuccess && !showGoogleNameModal) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose, loginSuccess, showGoogleNameModal]);

  const isValid = email.trim() !== '' && password.length >= 8;

  // ====================== تسجيل الدخول بجوجل المعدل ======================
  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      console.log("User signed in:", user);

      const verifiedRef = doc(db, 'verified_users', user.uid);
      const verifiedSnap = await getDoc(verifiedRef);

      if (!verifiedSnap.exists()) {
        setGoogleUserData(user);
        setShowGoogleNameModal(true);
      } else {
        const data = verifiedSnap.data();
        const displayName = data.firstName || user.displayName?.split(' ')[0] || t('مستخدم', 'User');
        
        setFirstName(displayName);
        setLoginSuccess(true);
        setTimeout(() => onClose(), 2500);
      }
    } catch (err) {
      // التعامل الذكي مع أخطاء النوافذ المنبثقة
      if (err.code === 'auth/popup-closed-by-user') {
        console.warn("User closed the popup");
        // لا نعرض خطأ مخيف، فقط نلغي حالة التحميل
      } else if (err.code === 'auth/cancelled-popup-request') {
        console.warn("Popup request cancelled");
      } else {
        console.error("Sign in error:", err);
        setError(t(
          'حدث خطأ أثناء تسجيل الدخول بجوجل. حاول مرة أخرى.',
          'Error signing in with Google. Please try again.'
        ));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ====================== باقي الدوال كما هي ======================
  const handleGoogleNameSubmit = async ({ firstName, lastName }) => {
    if (!googleUserData) return;
    try {
      const fullName = `${firstName} ${lastName}`;
      await setDoc(doc(db, 'verified_users', googleUserData.uid), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        fullName,
        email: googleUserData.email,
        photoURL: googleUserData.photoURL,
        createdAt: new Date().toISOString(),
        verifiedAt: new Date().toISOString(),
        last_login_at: new Date().toISOString(),
      });
      setFirstName(firstName);
      setShowGoogleNameModal(false);
      setLoginSuccess(true);
      setTimeout(() => onClose(), 2200);
    } catch (err) {
      console.error(err);
      setError(t('حدث خطأ أثناء حفظ البيانات', 'Error saving data'));
    }
  };

  const handleGoogleNameModalClose = async () => {
    setShowGoogleNameModal(false);
    setGoogleUserData(null);
    try {
      await auth.signOut();
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      if (!user.emailVerified) {
        await auth.signOut();
        setError(t('يرجى التحقق من بريدك الإلكتروني أولاً.', 'Please verify your email first.'));
        setIsLoading(false);
        return;
      }
      const verifiedUserDoc = await getDoc(doc(db, 'verified_users', user.uid));
      let displayName = t('مستخدم', 'User');
      if (verifiedUserDoc.exists()) {
        const data = verifiedUserDoc.data();
        displayName = data.firstName || data.fullName?.split(' ')[0] || displayName;
      }
      const unverifiedDocRef = doc(db, 'unverified_users', user.uid);
      const unverifiedSnap = await getDoc(unverifiedDocRef);
      if (unverifiedSnap.exists()) {
        await setDoc(doc(db, 'verified_users', user.uid), {
          ...unverifiedSnap.data(),
          verifiedAt: new Date().toISOString(),
        });
        await deleteDoc(unverifiedDocRef);
      }
      await updateDoc(doc(db, 'verified_users', user.uid), {
        last_login_at: new Date().toISOString(),
      });
      setFirstName(displayName);
      setLoginSuccess(true);
      setTimeout(() => onClose(), 2500);
    } catch (err) {
      setError(currentLanguage === 'ar' 
        ? 'البريد الإلكتروني أو كلمة المرور غير صحيحة.' 
        : 'Incorrect email or password.');
    } finally {
      setIsLoading(false);
    }
  };

  if (loginSuccess) {
    return (
      <div className="login-modal-overlay">
        <div className={`login-modal success ${currentLanguage === 'ar' ? 'ar' : 'en'}`} ref={modalRef}>
          <div className="welcome-content">
            <div className="welcome-emoji" role="img" aria-label="waving hand">👋</div>
            <h2 className="welcome-title">{t('مرحباً!', 'Welcome')}</h2>
            <p className="welcome-name">
              {currentLanguage === 'ar'
                ? `عزيزنا ${firstName}، نحن سعداء لرؤيتك.`
                : `Dear ${firstName}, we are glad to see you.`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {!showGoogleNameModal && (
        <div className="login-modal-overlay">
          <div className={`login-modal ${currentLanguage === 'ar' ? 'ar' : 'en'}`} ref={modalRef}>
            <button className="login-modal-close" onClick={onClose} aria-label="Close">×</button>
            <div className="login-modal-logo">
              <img src="/assets/logo.png" alt="ROOMATE" />
            </div>
            <h2 className="login-modal-title">{t('سجل الدخول لحسابك', 'Login to your account')}</h2>
            <form className="login-modal-form" onSubmit={handleSubmit}>
              <div className="login-modal-field">
                <label>{t('البريد الإلكتروني', 'Email Address')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('أدخل بريدك الإلكتروني', 'Enter your email')}
                  dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
                  disabled={isLoading}
                />
              </div>
              <div className="login-modal-field">
                <label>{t('كلمة المرور', 'Password')}</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('أدخل كلمة المرور', 'Enter your password')}
                  dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
                  disabled={isLoading}
                />
              </div>
              <div className="password-toggle-container">
                <label className="password-toggle-label">
                  <input
                    type="checkbox"
                    checked={showPassword}
                    onChange={(e) => setShowPassword(e.target.checked)}
                  />
                  <span className="password-toggle-text">{t('إظهار كلمة المرور', 'Show password')}</span>
                </label>
              </div>
              <button
                type="submit"
                className={`login-modal-submit ${isValid && !isLoading ? '' : 'disabled'}`}
                disabled={!isValid || isLoading}
              >
                {isLoading ? t('جاري تسجيل الدخول...', 'Logging in...') : t('تسجيل الدخول', 'Login')}
              </button>
            </form>
            <div className="divider"><span>{t('أو', 'OR')}</span></div>
            <button 
              className="google-signin-btn"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              type="button"
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" width="20" />
              {t('تسجيل الدخول بجوجل', 'Sign in with Google')}
            </button>
            {error && <div className="login-modal-error">{error}</div>}
            <div className="login-modal-links">
              <button type="button" onClick={() => onClose('forgot')} className="login-modal-link">
                {t('نسيت كلمة المرور؟', 'Forgot your password?')}
              </button>
              <button type="button" onClick={() => onClose('register')} className="login-modal-link">
                {t('إنشاء حساب جديد', 'Create new account')}
              </button>
            </div>
          </div>
        </div>
      )}
      {showGoogleNameModal && (
        <GoogleNameModal
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          googleUser={googleUserData}
          onSubmit={handleGoogleNameSubmit}
          onClose={handleGoogleNameModalClose}
        />
      )}
    </>
  );
};

export default LoginModal;